import { useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Box, Button, FormControlLabel, Switch } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Controller, useForm } from 'react-hook-form'

import {
  Card,
  GovernanceProjectTemplateSelect,
  LoadingFeedback,
} from 'components'
import { GeneralInformation, NotificationsAndDeadlines } from './components'

import { useFetch, useSnackbar } from 'hooks'
import helpers from 'helpers'
import schema from './schema'
import * as service from 'service'
import { routes } from 'Routes'

const PaymentServiceTemplateForm = ({
  paymentServiceTemplate,
  isEdit = false,
}) => {
  const [loading, setLoading] = useState(false)
  const [emails, setEmails] = useState([])
  const history = useHistory()
  const snackbar = useSnackbar()

  const { handleSubmit, control, errors, watch } = useForm({
    validationSchema: schema,
    defaultValues: {
      name: paymentServiceTemplate?.name || '',
      description: paymentServiceTemplate?.description || '',
      active: paymentServiceTemplate ? paymentServiceTemplate?.active : true,
      price:
        helpers.formatters.currencyInput(paymentServiceTemplate?.price) || '',
      recommendedDeadlineValue:
        paymentServiceTemplate?.recommendedDeadlineValue || '',
      recommendedDeadlinePeriod:
        paymentServiceTemplate?.recommendedDeadlinePeriod || 'month',
      renewable: paymentServiceTemplate?.renewable ?? false,
      enableAdvance: paymentServiceTemplate
        ? paymentServiceTemplate?.enableAdvance
        : true,
      valueAdvance: paymentServiceTemplate?.valueAdvance || '',
      periodAdvance: paymentServiceTemplate?.periodAdvance || 'day',
      frequencyAdvance: paymentServiceTemplate?.frequencyAdvance || 'weekly',
      isGovernanceReviewType:
        paymentServiceTemplate?.isGovernanceReviewType ?? false,
      governanceProjectTemplateId:
        paymentServiceTemplate?.governanceProjectTemplate?.id ?? '',
    },
  })

  const isGovernanceReview = !!watch('isGovernanceReviewType')
  const {
    response: eligibleTemplatesResponse,
    error: eligibleTemplatesError,
    isLoading: eligibleTemplatesLoading,
  } = useFetch(
    service.dponet.paymentServiceTemplates
      .getEligibleGovernanceProjectTemplates,
    {},
    [],
    isGovernanceReview,
    true,
  )
  const savedGovernanceProjectTemplateId =
    paymentServiceTemplate?.governanceProjectTemplate?.id
  const isGovernanceProjectTemplateChanged =
    isGovernanceReview &&
    !!savedGovernanceProjectTemplateId &&
    watch('governanceProjectTemplateId') !== savedGovernanceProjectTemplateId

  const onSubmit = async (data) => {
    setLoading(true)

    const paymentService = {
      ...data,
      price: helpers.formatters.currencyOutput(data.price),
    }

    if (!isGovernanceReview) {
      // na edição o backend exige template null explícito ao desligar a flag
      if (isEdit) {
        paymentService.governanceProjectTemplateId = null
      } else {
        delete paymentService.governanceProjectTemplateId
      }
    }

    try {
      if (isEdit) {
        await service.dponet.paymentServiceTemplates.update({
          paymentServiceTemplateId: paymentServiceTemplate?.id,
          paymentService,
        })
      } else {
        const response = await service.dponet.paymentServiceTemplates.create({
          paymentService,
        })
        const newPaymentServiceTemplateId = response?.data?.paymentService?.id

        await Promise.all(
          emails.map((email) =>
            service.dponet.paymentServiceTemplates.createBillingEmail({
              paymentServiceTemplateId: newPaymentServiceTemplateId,
              email,
            }),
          ),
        )
      }

      snackbar.open({
        message: `Serviço ${isEdit ? 'atualizado' : 'criado'} com sucesso!`,
        variant: 'success',
      })
      history.push(routes.paymentServiceTemplates.all)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          `Falha ao ${isEdit ? 'atualizar' : 'criar'} o serviço!`,
        variant: 'error',
      })
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={loading} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <GeneralInformation control={control} errors={errors} />
        <Card title="Revisão de Projetos de Governança">
          <FormControlLabel
            control={
              <Controller
                as={<Switch color="primary" />}
                name="isGovernanceReviewType"
                control={control}
                onChange={([event]) => event.target.checked}
              />
            }
            label="Serviço de Revisão de Projetos de Governança"
          />
          {isGovernanceReview && (
            <Box mt={2}>
              <GovernanceProjectTemplateSelect
                control={control}
                errors={errors}
                eligibleTemplates={
                  eligibleTemplatesResponse?.data?.governanceProjectTemplates
                }
                loading={eligibleTemplatesLoading}
                error={eligibleTemplatesError}
                savedTemplate={
                  paymentServiceTemplate?.governanceProjectTemplate
                }
              />
              {isGovernanceProjectTemplateChanged && (
                <Box mt={2}>
                  <Alert severity="warning">
                    Ao salvar, os projetos cobertos pela Revisão serão
                    atualizados imediatamente em todos os contratos ativos que
                    usam este serviço: os projetos do novo template passam a ser
                    cobertos e os do template anterior deixam de ser.
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </Card>
        <NotificationsAndDeadlines
          control={control}
          errors={errors}
          isEdit={isEdit}
          paymentServiceTemplateId={paymentServiceTemplate?.id}
          emails={emails}
          setEmails={setEmails}
        />
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Box mr={2}>
            <Button
              variant="outlined"
              onClick={() => history.push(routes.paymentServiceTemplates.all)}
            >
              Cancelar
            </Button>
          </Box>
          <Button color="primary" variant="contained" type="submit">
            Salvar
          </Button>
        </Box>
      </form>
    </>
  )
}

PaymentServiceTemplateForm.propTypes = {
  paymentServiceTemplate: PropTypes.object,
  isEdit: PropTypes.bool,
}

export default PaymentServiceTemplateForm
